import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { MdArrowUpward, MdArrowDownward, MdDragIndicator } from 'react-icons/md';
import type { TQuizQuestion, TQuizOption } from '@onesource/services';

interface OrderingQuestionProps {
  question: TQuizQuestion;
  orderingSequence: string[];
  onOrderChange: (sequence: string[]) => void;
  disabled?: boolean;
}

/**
 * Ordering Question Component
 * Renders a list of items that can be reordered using up/down buttons
 */
export const OrderingQuestion = ({
  question,
  orderingSequence,
  onOrderChange,
  disabled = false,
}: OrderingQuestionProps) => {
  // Initialize order from props or from options' original positions
  const [orderedItems, setOrderedItems] = useState<TQuizOption[]>([]);

  useEffect(() => {
    if (orderingSequence.length > 0) {
      // Use the provided sequence
      const ordered = orderingSequence
        .map(id => question.options.find(o => o.id === id))
        .filter((o): o is TQuizOption => o !== undefined);
      setOrderedItems(ordered);
    } else {
      // Initialize with options in their original position order
      const sorted = [...question.options].sort((a, b) => a.position - b.position);
      setOrderedItems(sorted);
      // Notify parent of initial order
      onOrderChange(sorted.map(o => o.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only initialize when question options change
  }, [question.options]);

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (disabled) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= orderedItems.length) {
      return;
    }

    const newItems = [...orderedItems];
    const [removed] = newItems.splice(index, 1);
    newItems.splice(newIndex, 0, removed);

    setOrderedItems(newItems);
    onOrderChange(newItems.map(item => item.id));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {orderedItems.map((option, index) => (
        <Box
          key={option.id}
          sx={{
            p: 2,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: disabled ? 'default' : 'grab',
            transition: 'all 0.2s',
            border: '2px solid transparent',
            '&:hover': disabled
              ? {}
              : {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                },
          }}
        >
          {/* Drag indicator */}
          <Box sx={{ color: '#999', display: 'flex', alignItems: 'center' }}>
            <MdDragIndicator size={20} />
          </Box>

          {/* Position number + text */}
          <Typography sx={{ flex: 1, lineHeight: 1.5 }}>
            <Box component="span" sx={{ color: '#ff4d6d', fontWeight: 'bold' }}>
              {index + 1}.
            </Box>{' '}
            {option.optionText}
          </Typography>

          {/* Up/Down buttons */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => moveItem(index, 'up')}
              disabled={disabled || index === 0}
              sx={{
                width: 32,
                height: 32,
                border: '1px solid #ddd',
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255, 77, 109, 0.1)' },
                '&:disabled': { opacity: 0.4 },
              }}
            >
              <MdArrowUpward size={16} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => moveItem(index, 'down')}
              disabled={disabled || index === orderedItems.length - 1}
              sx={{
                width: 32,
                height: 32,
                border: '1px solid #ddd',
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255, 77, 109, 0.1)' },
                '&:disabled': { opacity: 0.4 },
              }}
            >
              <MdArrowDownward size={16} />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
